/**
 * EdgeStyleContextMenu - Context menu for changing edge styles
 * 
 * Shows when right-clicking on an edge/connection in the flow
 * Allows changing between different edge types (straight, bezier, etc.)
 */

import React, { useEffect, useRef } from 'react';
import { Edge } from '@xyflow/react';

export interface EdgeStyleContextMenuProps {
  edge: Edge | null;
  position: { x: number; y: number };
  isOpen: boolean;
  onClose: () => void;
  onChangeStyle: (edgeId: string, newType: string) => void;
  onToggleAnimated?: (edgeId: string) => void;
}

const edgeStyles = [
  { type: 'default', label: 'Default (Bezier)', icon: '〰️' },
  { type: 'straight', label: 'Straight', icon: '—' },
  { type: 'step', label: 'Step', icon: '⌐' },
  { type: 'smoothstep', label: 'Smooth Step', icon: '⌙' },
  { type: 'simplebezier', label: 'Simple Bezier', icon: '⌢' },
];

export function EdgeStyleContextMenu({
  edge,
  position,
  isOpen,
  onClose,
  onChangeStyle,
  onToggleAnimated,
}: EdgeStyleContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add a small delay to prevent immediate closing when menu first opens
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !edge) return null;

  const handleStyleClick = (styleType: string) => {
    onChangeStyle(edge.id, styleType);
    onClose();
  };

  const currentType = edge.type || 'default';

  return (
    <div
      ref={menuRef}
      className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[10000] min-w-[200px]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Edge Style
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
          {edge.data?.label || edge.id}
        </div>
      </div>

      {/* Style options */}
      <div className="py-1">
        {edgeStyles.map((style) => (
          <button
            key={style.type}
            onClick={() => handleStyleClick(style.type)}
            className={`
              w-full px-4 py-2 text-left text-sm flex items-center gap-3
              hover:bg-gray-100 dark:hover:bg-gray-700
              transition-colors
              ${currentType === style.type ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}
            `}
          >
            <span className="text-lg">{style.icon}</span>
            <span className="flex-1">{style.label}</span>
            {currentType === style.type && (
              <span className="text-blue-600 dark:text-blue-400">✓</span>
            )}
          </button>
        ))}
      </div>

      {/* Additional options */}
      <div className="border-t border-gray-200 dark:border-gray-700 py-1">
        <button
          onClick={() => {
            if (onToggleAnimated) {
              onToggleAnimated(edge.id);
            }
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
        >
          <span className="text-lg">⚡</span>
          <span className="flex-1">Animated</span>
          {edge.animated && (
            <span className="text-blue-600 dark:text-blue-400">✓</span>
          )}
        </button>
      </div>
    </div>
  );
}

export default EdgeStyleContextMenu;
